/**
 * ============================================================================
 * IMAGE BLOCK LOADER
 * Handles loading states for image-block components
 * ============================================================================
 * 
 * Features:
 * - Detects when images/videos finish loading
 * - Adds 'is-loaded' class for CSS transitions
 * - Uses Intersection Observer for lazy initialization
 * - Handles dynamically added content (MutationObserver)
 */

(function() {
  'use strict';

  /**
   * Initialize a single image block
   * @param {HTMLElement} block - The .image-block element
   */
  function initImageBlock(block) {
    if (block.dataset.initialized === 'true') return;
    block.dataset.initialized = 'true';

    const img = block.querySelector('.image-block__img');
    const video = block.querySelector('.image-block__video');

    // Handle image loading
    if (img) {
      if (img.complete && img.naturalHeight !== 0) {
        // Image already loaded (cached)
        markAsLoaded(block, img);
      } else {
        img.addEventListener('load', function() {
          markAsLoaded(block, img);
        }, { once: true });

        img.addEventListener('error', function() {
          // Still mark as loaded to hide shimmer
          markAsLoaded(block, img);
        }, { once: true });
      }
    }

    // Handle video loading
    if (video) {
      if (video.readyState >= 3) {
        // Video already has enough data
        markAsLoaded(block, video);
      } else {
        video.addEventListener('canplay', function() {
          markAsLoaded(block, video);
        }, { once: true });

        video.addEventListener('loadeddata', function() {
          markAsLoaded(block, video);
        }, { once: true });

        video.addEventListener('error', function() {
          markAsLoaded(block, video);
        }, { once: true });
      }
    }

    // If no media, mark as loaded immediately
    if (!img && !video) {
      block.classList.add('is-loaded');
    }
  }

  /**
   * Mark block and media element as loaded
   * @param {HTMLElement} block - The .image-block element
   * @param {HTMLElement} media - The img or video element
   */
  function markAsLoaded(block, media) {
    media.dataset.loaded = 'true';
    block.classList.add('is-loaded');
  }

  /**
   * Initialize all image blocks with Intersection Observer
   * Only initialize blocks when they enter the viewport
   */
  function initWithIntersectionObserver() {
    const blocks = document.querySelectorAll('.image-block:not([data-initialized="true"])');
    
    if (!blocks.length) return;

    // Check for IntersectionObserver support
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              initImageBlock(entry.target);
              observer.unobserve(entry.target);
            }
          });
        },
        {
          rootMargin: '100px 0px', // Start loading 100px before entering viewport
          threshold: 0
        }
      );

      blocks.forEach((block) => observer.observe(block));
    } else {
      // Fallback: initialize all immediately
      blocks.forEach(initImageBlock);
    }
  }

  /**
   * Watch for dynamically added image blocks
   */
  function watchForNewBlocks() {
    if (!('MutationObserver' in window)) return;

    const observer = new MutationObserver((mutations) => {
      let hasNewBlocks = false;
      
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.classList?.contains('image-block')) {
              hasNewBlocks = true;
            } else if (node.querySelector?.('.image-block')) {
              hasNewBlocks = true;
            }
          }
        });
      });

      if (hasNewBlocks) {
        // Debounce to batch multiple additions
        clearTimeout(window.imageBlockDebounce);
        window.imageBlockDebounce = setTimeout(initWithIntersectionObserver, 100);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initWithIntersectionObserver();
      watchForNewBlocks();
    });
  } else {
    initWithIntersectionObserver();
    watchForNewBlocks();
  }

  // Expose for manual initialization (useful for AJAX content)
  window.ImageBlockLoader = {
    init: initWithIntersectionObserver,
    initBlock: initImageBlock
  };

})();
